import React, { createRef, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Form from 'react-form-controlled';
import Validate from 'react-form-controlled-validate';
import { AsYouTypeFormatter } from 'google-libphonenumber/dist/libphonenumber';
import { FormattedMessage, FormattedHTMLMessage, useIntl } from 'react-intl';
import { selectInput, textInput } from './FormInput';
import { Screens, pruneObj } from './constants';
import MessageDefinitions from './MessageDefinitions';
import MessageIfPresent from '@ignitesales/neuro-application/src/components/MessageIfPresent';

const ContactForm = ({
  children,
  shouldValidateOnChange,
  shouldValidateOnBlur,
  useAppointmentFlow,
  isProcessing,
  contactLead,
  onContactFormSubmit,
  onComplete,
  validators,
  changeScreen,
  fields = ContactForm.defaultConsumerFields,
  fieldsForAnswerService = [],
  schema = ContactForm.defaultConsumerSchema,
  submitMessage,
}) => {
  const formFields = [...fields, ...fieldsForAnswerService];
  const formRef = useRef(null);
  const formsRef = useRef(null);
  const inputsRef = useRef(formFields.map(() => createRef()));

  const [errors, setErrors] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [form, setForm] = useState(contactLead);
  const intl = useIntl();

  const validateForm = async (state, component) => {
    const prunedState = Object.keys(state).reduce(
      (agg, key) =>
        key === component.props.name ? { [key]: state[key] } : agg,
      {}
    );
    const formErrors =
      (prunedState && (await formRef.current.validate(prunedState))) || [];
    const isComponentError = formErrors.reduce(
      (agg, error) =>
        error.path === component.props.name
          ? { key: error.path, errors: [error.message] }
          : agg,
      false
    );
    const isStateError = errors.reduce(
      (agg, e, idx) => (e.key === component.props.name ? (idx += 1) : agg),
      false
    );
    let fieldErrors = errors;

    if (isComponentError && !isStateError) {
      fieldErrors = [...errors, isComponentError];
    } else if (!isComponentError && isStateError) {
      fieldErrors = [
        ...errors.slice(0, isStateError - 1),
        ...errors.slice(isStateError),
      ];
    }

    setErrors(fieldErrors);
    setShowErrors(fieldErrors?.length > 0);
  };

  const handleBlur = (e, idx) => {
    validateForm(formsRef.current.value, inputsRef.current[idx].current);
  };

  const handleValueChange = (state, component) => {
    const lead = { ...state };
    if (lead.phone) {
      lead.phone = state.phone ? state.phone.replace(/\D/g, '') : '';
    }

    shouldValidateOnChange && validateForm(state, component);
    setForm(lead);
  };

  const formSubmitted = (state) => {
    const lead = { ...state };
    lead.phone = state.phone ? state.phone.replace(/\D/g, '') : '';

    onContactFormSubmit(lead, fieldsForAnswerService);
    if (!useAppointmentFlow) {
      onComplete({ type: 'contact' });
    } else {
      changeScreen(Screens.APPT_LOCATION);
    }
  };

  const formValid = !errors.length;
  const value = pruneObj(form);

  if (value.phone) {
    const formatter = new AsYouTypeFormatter('US');
    let phoneNumber = null;

    for (const n of value.phone || []) {
      phoneNumber = formatter.inputDigit(n);
    }

    value.phone = phoneNumber;
  }

  const fieldErrors = {};

  errors.forEach((error) => {
    fieldErrors[error.key] = true;
  });

  const renderFormFields = formFields.map((field, idx) => {
    let type = 'text';
    switch (field) {
      case 'email':
        type = 'email';
        break;

      case 'phone':
        type = 'tel';
        break;

      case 'preferredMethodOfContact':
        return selectInput({
          formatMessage: intl.formatMessage,
          name: field,
          options: {
            option1: MessageDefinitions[`${field}Option1`],
            option2: MessageDefinitions[`${field}Option2`],
          },
          labelMessage: MessageDefinitions[field],
          placeholderMessage: MessageDefinitions[`${field}Placeholder`],
          errorMessage: MessageDefinitions[`${field}Error`],
          hasError: showErrors && fieldErrors[field],
          ref: inputsRef.current[idx],
          ...(shouldValidateOnBlur && { onBlur: (e) => handleBlur(e, idx) }),
        });

      case 'preferredTime':
        return selectInput({
          formatMessage: intl.formatMessage,
          name: field,
          options: {
            option1: MessageDefinitions[`${field}Option1`],
            option2: MessageDefinitions[`${field}Option2`],
            option3: MessageDefinitions[`${field}Option3`],
            option4: MessageDefinitions[`${field}Option4`],
          },
          labelMessage: MessageDefinitions[field],
          placeholderMessage: MessageDefinitions[`${field}Placeholder`],
          errorMessage: MessageDefinitions[`${field}Error`],
          hasError: showErrors && fieldErrors[field],
          ref: inputsRef.current[idx],
          ...(shouldValidateOnBlur && { onBlur: (e) => handleBlur(e, idx) }),
        });

      default:
        break;
    }
    return textInput({
      formatMessage: intl.formatMessage,
      type,
      name: field,
      labelMessage: MessageDefinitions[field],
      placeholderMessage: MessageDefinitions[`${field}Placeholder`],
      errorMessage: MessageDefinitions[`${field}Error`],
      hasError: showErrors && fieldErrors[field],
      ref: inputsRef.current[idx],
      ...(shouldValidateOnBlur && { onBlur: (e) => handleBlur(e, idx) }),
    });
  });

  const buttonMessage = isProcessing ? (
    <FormattedMessage {...MessageDefinitions.loading} />
  ) : useAppointmentFlow ? (
    <FormattedMessage {...MessageDefinitions.continue} />
  ) : submitMessage ? submitMessage : (
    <FormattedMessage {...MessageDefinitions.submit} />
  );

  const disclosureMessage = (
    <MessageIfPresent {...MessageDefinitions.formDisclosure}>
      <div className="form-disclosure">
        <FormattedHTMLMessage
          {...MessageDefinitions.formDisclosure}
          tagName="span"
        />
      </div>
    </MessageIfPresent>
  );

  React.useMemo(() => {
    return (
      Object.entries(ContactForm.defaultValidators).forEach(([key, value]) => {
        if (schema?.properties?.[key]) {
          schema.properties[key] = {
            ...schema.properties[key],
            ...(validators?.[key] || value),
          };
        }
      }),
      [schema, validators]
    );
  });

  return (
    <Validate schema={schema} ref={formRef}>
      <Form
        value={value}
        ref={formsRef}
        className="contactMe__form"
        onChange={handleValueChange}
        onSubmit={formSubmitted}
      >
        <div className="form-body">
          {renderFormFields}
          {children}
        </div>
        {disclosureMessage}
        <div className="form-actions" onClick={() => setShowErrors(true)}>
          <button
            type="submit"
            className="form-submitBtn"
            disabled={!formValid || isProcessing}
          >
            {buttonMessage}
          </button>
        </div>
      </Form>
    </Validate>
  );
};

ContactForm.propTypes = {
  contactLead: PropTypes.object,
  fields: PropTypes.arrayOf(PropTypes.string),
  schema: PropTypes.object,
  useAppointmentFlow: PropTypes.bool,
  onContactFormSubmit: PropTypes.func,
  contactMeScreenChanged: PropTypes.func,
};

ContactForm.defaultBusinessSchema = {
  type: 'object',
  properties: {
    firstName: {
      type: 'string',
    },
    lastName: {
      type: 'string',
    },
    businessName: {
      type: 'string',
    },
    email: {
      type: 'string',
    },
    phone: {
      type: 'string',
    },
    customerId: {
      type: 'string',
    },
    preferredTime: {
      type: 'string',
    },
  },
  required: ['firstName', 'lastName', 'email', 'phone', 'businessName'],
};

ContactForm.defaultConsumerSchema = {
  type: 'object',
  properties: {
    firstName: {
      type: 'string',
    },
    lastName: {
      type: 'string',
    },
    email: {
      type: 'string',
    },
    phone: {
      type: 'string',
    },
    preferredMethodOfContact: {
      type: 'string',
    },
    customerId: {
      type: 'string',
    },
    preferredTime: {
      type: 'string',
    },
  },
  required: ['firstName', 'lastName', 'email', 'phone'],
};

ContactForm.defaultConsumerFields = [
  'firstName',
  'lastName',
  'email',
  'phone',
];

ContactForm.defaultBusinessFields = [
  'firstName',
  'lastName',
  'businessName',
  'email',
  'phone',
];

ContactForm.defaultValidators = {
  firstName: {
    minLength: 1,
  },
  lastName: {
    minLength: 1,
  },
  businessName: {
    minLength: 1,
  },
  email: {
    pattern: '^(([^<>()\\[\\]\\\\.,;:\\s@"]+(\\.[^<>()\\[\\]\\\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])|(([a-zA-Z0-9]+\\.)+[a-zA-Z]{2,}))$',
  },
  phone: {
    pattern: '^\\([0-9]{3}\\)\\s*[0-9]{3}-[0-9]{4}$|^[0-9]{10}$',
  },
  preferredMethodOfContact: {
    enum: ['option1', 'option2'],
  },
  customerId: {
    minLength: 1,
  },
  preferredTime: {
    enum: ['option1', 'option2', 'option3', 'option4'],
  },
};

export default ContactForm;
